import { AuthProvider as AuthUiProvider, describeSessionEnd, useAuthMe } from '@h2hspark/auth-ui';
import * as React from 'react';
import { useNavigate } from 'react-router-dom';

import { getWebEnvironment } from '@/env';

import { AccountDataBoundary } from './account-data-boundary';
import { isGatedPathClaimed } from './gated-paths';
import { isMemberAppPublicPath } from './public-paths';

/**
 * Wraps the shared auth provider with this site's redirect-to-sign-in behavior.
 *
 * Only the member app under `/app` has a session; the marketing pages are public, so an
 * anonymous visitor there is the normal case rather than something to redirect.
 */
export function AuthProvider({
  children,
}: {
  readonly children: React.ReactNode;
}): React.ReactElement {
  const environment = React.useMemo(() => getWebEnvironment(), []);
  // The API mounts its auth router under `/api`, and the auth-ui client requests paths
  // like `/auth/login` relative to this base.
  const apiBase = React.useMemo(
    () => `${environment.apiUrl.replace(/\/$/, '')}/api`,
    [environment.apiUrl],
  );

  return (
    <AuthUiProvider apiBase={apiBase}>
      <UnauthenticatedRedirector />
      <SignedInAccountDataBoundary />
      {children}
    </AuthUiProvider>
  );
}

/** Feeds `AccountDataBoundary` who is signed in: an id, `null` signed out, `undefined` settling. */
function SignedInAccountDataBoundary(): React.ReactElement {
  const { user, loading } = useAuthMe();
  return <AccountDataBoundary accountId={loading ? undefined : (user?.userId ?? null)} />;
}

/**
 * Sends a member whose session has ended back to sign-in, carrying where they were so they
 * resume rather than restart. Scoped to `/app` — leaving the marketing pages alone.
 *
 * ## What changed with `MEM-019`, and why
 *
 * The exempt list used to be five `startsWith` calls written out here. It is now
 * `auth/public-paths.ts`, because the list and the router's public routes were two copies of one
 * fact and only one of them was visible in review.
 *
 * The list also grew Discovery. `MEM-019` AC-1 makes the Discover list and every public event's
 * detail page available with no account, so a signed-out visitor on `/app` is the ordinary case
 * rather than a session that expired — and redirecting them to a login form would have been the
 * whole feature undone from the client side.
 *
 * This stays as belt-and-braces for a gated screen somebody forgets to wrap in `ProtectedRoute`.
 *
 * ## It did not win by rendering first, and that sentence used to live here
 *
 * The paragraph this replaced claimed that where `ProtectedRoute` is present it *"wins by
 * rendering first"*. It does not. An effect that calls `navigate` beats a component that merely
 * rendered, so **every** gated screen redirected to a bare login form — the exact behaviour
 * `MEM-019` AC-3 was written to replace, and the interstitial was never seen by anybody.
 *
 * The gate now claims its path while it is mounted (`gated-paths.ts`) and this stands down for a
 * claimed path, so the belt only tightens where there is no gate at all — which is what it was
 * always described as doing.
 */
function UnauthenticatedRedirector(): null {
  const navigate = useNavigate();
  const { user, loading, endedBecause } = useAuthMe();

  React.useEffect(() => {
    if (loading) return;
    if (user !== null) return;
    if (typeof window === 'undefined') return;
    const path = window.location.pathname;
    if (!path.startsWith('/app')) return;
    if (isMemberAppPublicPath(path)) return;
    // A mounted `ProtectedRoute` is already showing the Sign-in required interstitial for this
    // path and carries `next` itself. Redirecting over it is what broke `MEM-019` AC-3.
    if (isGatedPathClaimed(path)) return;
    const next = `${window.location.pathname}${window.location.search}`;
    // When the platform ended the session rather than the person, sign-in says why (`PLT-014`
    // AC-7): the login page renders `error` above the form. A gated screen's own
    // `ProtectedRoute` shows the same sentence on its interstitial instead.
    const because = endedBecause === null ? '' : `&error=${encodeURIComponent(describeSessionEnd(endedBecause))}`;
    navigate(`/app/login?next=${encodeURIComponent(next)}${because}`, { replace: true });
  }, [user, loading, endedBecause, navigate]);

  return null;
}

export interface MemberSession {
  readonly userId: string;
  readonly emailAddress: string | undefined;
  readonly displayName: string | undefined;
  /**
   * The sign-in's groups. `organizer` is the one every organizer route requires, which is how the
   * organizer header tells a signed-in organizer from a member (`CMS-026`).
   */
  readonly groups: ReadonlyArray<string>;
}

export function useMemberSession(): {
  readonly status: 'loading' | 'authenticated' | 'unauthenticated';
  readonly session: MemberSession | null;
  readonly reload: () => Promise<void>;
} {
  const { user, loading, reload } = useAuthMe();
  const status: 'loading' | 'authenticated' | 'unauthenticated' = loading
    ? 'loading'
    : user === null
      ? 'unauthenticated'
      : 'authenticated';
  const session: MemberSession | null =
    user === null
      ? null
      : {
          userId: user.userId,
          emailAddress: user.emailAddress ?? undefined,
          displayName: user.displayName ?? undefined,
          groups: user.groups,
        };
  return { status, session, reload };
}
